import {
  type RawTelemetryData,
  type GpsReading,
  type GyroscopeReading,
  type AccelerometerReading,
  calculateDistance,
} from '@pitbrain/shared'
import telemetry from '../telemetry/telemetry'
import type {
  GoProRoot,
  GoProGpsSample,
  GoProGps9Sample,
  GoProGyroSample,
  GoProAcclSample,
} from './GoProTelemetryTypes'

/**
 * A GPS sample normalized across the GPS5 (up to Hero 11) and GPS9 (Hero 13+)
 * stream formats. Cameras emit one or the other; GPS9 files have no GPS5 stream.
 */
interface NormalizedGpsSample {
  latitude: number
  longitude: number
  altitude: number
  speedMs: number
  date: string
}

const normalizeGps5 = (s: GoProGpsSample): NormalizedGpsSample | null => {
  if (s['GPS (Lat.) [deg]'] == null || s['GPS (Long.) [deg]'] == null)
    return null
  return {
    latitude: s['GPS (Lat.) [deg]'],
    longitude: s['GPS (Long.) [deg]'],
    altitude: s['GPS (Alt.) [m]'],
    speedMs: s['GPS (2D speed) [m/s]'],
    date: s.date,
  }
}

// GPS9 carries fix and DOP per sample instead of the per-stream GPSF/GPSP
// stickies that the GPS5Fix/GPS5Precision options filter on, so the
// equivalent thresholds are applied here (fix ≥ 2, DOP ≤ 5 ≙ GPSP ≤ 500).
const GPS9_MIN_FIX = 2
const GPS9_MAX_DOP = 5

const normalizeGps9 = (s: GoProGps9Sample): NormalizedGpsSample | null => {
  if (s['GPS (Lat.) [deg]'] == null || s['GPS (Long.) [deg]'] == null)
    return null
  const fix = s['GPS (fix) [deg]']
  const dop = s['GPS (DOP) [deg]']
  if (fix != null && fix < GPS9_MIN_FIX) return null
  if (dop != null && dop > GPS9_MAX_DOP) return null
  return {
    latitude: s['GPS (Lat.) [deg]'],
    longitude: s['GPS (Long.) [deg]'],
    altitude: s['GPS (Alt.) [m]'],
    speedMs: s['GPS (2D) [m/s]'],
    date: s.date,
  }
}

/**
 * True UTC of a GPS9 sample from its embedded satellite time
 * (days since 2000-01-01 + seconds of day). The MP4/camera clock that
 * otherwise stamps sample dates is camera-local wall time and drifts, so
 * sessions built from it end up hours off; satellite time is authoritative.
 * Returns null if the fields are missing.
 */
const gps9SatelliteTimeMs = (s: GoProGps9Sample): number | null => {
  const days = s['GPS (days) [deg]']
  const secs = s['GPS (secs) [s]']
  if (days == null || secs == null) return null
  return Date.UTC(2000, 0, 1) + days * 86400000 + secs * 1000
}

/**
 * Milliseconds to add to every camera-clock-derived timestamp so the session
 * lands on true UTC. Computed from the first GPS9 sample with satellite time;
 * a uniform shift preserves the relative alignment of the GPS/gyro/accel
 * streams. Zero for GPS5 recordings (no per-sample satellite time available).
 */
const clockCorrectionMs = (samples: GoProGps9Sample[]): number => {
  for (const s of samples) {
    const satMs = gps9SatelliteTimeMs(s)
    if (satMs != null && s.date != null) {
      return satMs - new Date(s.date).getTime()
    }
  }
  return 0
}

/**
 * Normalize gyroscope axes across camera generations. Older cameras label
 * each axis via STNM/ORIO, producing per-axis headers like
 * 'Gyroscope (x) [rad/s]'. Hero 13 emits a bare STNM ("Gyroscope") with no
 * ORIO, so headers degrade to '<name> [unit]', '(1)', '(2)' in the sensor's
 * native ORIN order: z, x, y.
 */
export const gyroAxes = (g: GoProGyroSample) => ({
  x: g['Gyroscope (x) [rad/s]'] ?? g['(1)'] ?? Number.NaN,
  y: g['Gyroscope (y) [rad/s]'] ?? g['(2)'] ?? Number.NaN,
  z: g['Gyroscope (z) [rad/s]'] ?? g['Gyroscope [rad/s]'] ?? Number.NaN,
})

/** Normalize accelerometer axes across camera generations — see gyroAxes. */
export const acclAxes = (g: GoProAcclSample) => ({
  x: g['Accelerometer (x) [m/s2]'] ?? g['(1)'] ?? Number.NaN,
  y: g['Accelerometer (y) [m/s2]'] ?? g['(2)'] ?? Number.NaN,
  z: g['Accelerometer (z) [m/s2]'] ?? g['Accelerometer [m/s2]'] ?? Number.NaN,
})

/**
 * Parse GoPro telemetry extraction results into raw telemetry format.
 */
export const parseGoProToRawTelemetry = async (
  results: Array<{ rawData: any; timing: any }> // eslint-disable-line
): Promise<RawTelemetryData> => {
  const gps: GoProRoot = await telemetry(results, {
    stream: ['GPS5', 'GPS9'],
    repeatSticky: true,
    repeatHeaders: true,
    GPS5Fix: 2,
    GPS5Precision: 500,
  })
  const gyro: GoProRoot = await telemetry(results, {
    stream: 'GYRO',
    repeatSticky: true,
    repeatHeaders: true,
  })
  const accl: GoProRoot = await telemetry(results, {
    stream: 'ACCL',
    repeatSticky: true,
    repeatHeaders: true,
  })

  const isSample = (s: NormalizedGpsSample | null): s is NormalizedGpsSample =>
    s != null
  const gpsStreams = gps[1].streams
  const normalizedSamples: NormalizedGpsSample[] = gpsStreams.GPS9?.samples
    ?.length
    ? gpsStreams.GPS9.samples.map(normalizeGps9).filter(isSample)
    : (gpsStreams.GPS5?.samples ?? []).map(normalizeGps5).filter(isSample)

  const correctionMs = gpsStreams.GPS9?.samples?.length
    ? clockCorrectionMs(gpsStreams.GPS9.samples)
    : 0
  const toUtc = (date: string | Date) =>
    new Date(new Date(date).getTime() + correctionMs)

  let totalDistance = 0
  const gpsReadings: GpsReading[] = normalizedSamples.map((sample, index) => {
    if (index > 0) {
      const previousSample = normalizedSamples[index - 1]
      totalDistance += calculateDistance(
        previousSample.latitude,
        previousSample.longitude,
        sample.latitude,
        sample.longitude
      )
    }
    return {
      latitude: sample.latitude,
      longitude: sample.longitude,
      altitude: sample.altitude,
      speedMs: sample.speedMs,
      timestamp: toUtc(sample.date),
      distanceMeters: totalDistance,
    }
  })

  const gyroReadings: GyroscopeReading[] | undefined =
    gyro[1].streams.GYRO?.samples.map((g: GoProGyroSample) => ({
      ...gyroAxes(g),
      timestamp: toUtc(g.date),
      synchronizationTime: g.cts,
      unit: 'rad/s' as const,
    }))

  const acclReadings: AccelerometerReading[] | undefined =
    accl[1].streams.ACCL?.samples.map((g: GoProAcclSample) => ({
      ...acclAxes(g),
      timestamp: toUtc(g.date),
      cts: g.cts,
      unit: 'm/s²' as const,
    }))

  return {
    gps: gpsReadings,
    gyroscope: gyroReadings,
    accelerometer: acclReadings,
    recordingStartTime: gpsReadings[0]?.timestamp,
    // TODO: Get device version from file.
    device: {
      deviceName: 'GoPro',
      deviceType: 'GoPro',
      deviceVersion: '8',
    },
  }
}
